import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from './AnimatePresenceWrapper';
import { useCart } from '../../shared/context/CartContext';
import { useWishlist } from '../../shared/context/WishlistContext';

interface MobileMenuDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  collections: Array<{ id: string; name: string; handle: string }>;
}

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: ${props => props.theme.zIndex.modalBackdrop};
`;

const Drawer = styled(motion.div)`
  position: fixed;
  top: 0;
  inset-inline-start: 0;
  height: 100%;
  width: 300px;
  max-width: 80%;
  background-color: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.xl};
  z-index: ${props => props.theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

const DrawerHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  cursor: pointer;
  color: ${props => props.theme.colors.text.secondary};
  
  &:hover {
    color: ${props => props.theme.colors.text.primary};
  }
`;

const DrawerContent = styled.div`
  flex: 1;
  padding: ${props => props.theme.spacing.md};
`;

const MenuSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const MenuSectionTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text.primary};
`;

const MenuLink = styled(Link)`
  display: block;
  padding: ${props => props.theme.spacing.sm} 0;
  text-decoration: none;
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.base};
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  
  &:hover {
    color: ${props => props.theme.colors.black};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const MobileMenuDrawer: React.FC<MobileMenuDrawerProps> = ({ isOpen, onClose, collections }) => {
  const { t, i18n } = useTranslation();
  const { itemCount: cartCount } = useCart();
  const { itemCount: wishlistCount } = useWishlist();
  const isRtl = i18n.dir() === 'rtl';

  const handleLinkClick = () => {
    onClose();
  };

  const slideFrom = isRtl ? '100%' : '-100%';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <Drawer
            initial={{ x: slideFrom }}
            animate={{ x: 0 }}
            exit={{ x: slideFrom }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <DrawerHeader>
              <h2>{t('mobileMenu.title')}</h2>
              <CloseButton onClick={onClose} type="button">&times;</CloseButton>
            </DrawerHeader>
            <DrawerContent>
              <MenuSection>
                <MenuSectionTitle>{t('mobileMenu.shop')}</MenuSectionTitle>
                <MenuLink to="/products" onClick={handleLinkClick}>
                  {t('mobileMenu.allProducts')}
                </MenuLink>
                <MenuLink to="/products" onClick={handleLinkClick}>
                  {t('mobileMenu.shopByFit')}
                </MenuLink>
                <MenuLink to="/products" onClick={handleLinkClick}>
                  {t('mobileMenu.shopByShade')}
                </MenuLink>
              </MenuSection>
              
              <MenuSection>
                <MenuSectionTitle>{t('mobileMenu.collections')}</MenuSectionTitle>
                <MenuLink to="/collections" onClick={handleLinkClick}>
                  {t('mobileMenu.allCollections')}
                </MenuLink>
                <MenuLink to="/categories" onClick={handleLinkClick}>
                  {t('mobileMenu.allCategories')}
                </MenuLink>
                {collections.map((collection) => (
                  <MenuLink
                    key={collection.id}
                    to={`/products?collection=${collection.id}`}
                    onClick={handleLinkClick}
                  >
                    {collection.name}
                  </MenuLink>
                ))}
              </MenuSection>
              
              <MenuSection>
                <MenuSectionTitle>{t('mobileMenu.account')}</MenuSectionTitle>
                <MenuLink to="/wishlist" onClick={handleLinkClick}>
                  {t('mobileMenu.wishlist')} {wishlistCount > 0 && `(${wishlistCount})`}
                </MenuLink>
                <MenuLink to="/cart" onClick={handleLinkClick}>
                  {t('mobileMenu.cart')} {cartCount > 0 && `(${cartCount})`}
                </MenuLink>
                <MenuLink to="/returns" onClick={handleLinkClick}>
                  {t('mobileMenu.returns')}
                </MenuLink>
              </MenuSection>
            </DrawerContent>
          </Drawer>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileMenuDrawer;
